import React, { memo, useEffect, useRef, useState } from 'react';
import Field, { FieldProps } from './Field';
import classes from './style.module.scss';
import useField from './useField';

type MultiSelectOption = {
  text: string,
  value: string
}

export interface MultiSelectPropsDefault extends React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement> {
  options: MultiSelectOption[],
  defaultOptions: string[],
  optionChange: (selected: string[]) => void
}

export type SelectProps = MultiSelectPropsDefault & FieldProps

export type MultiSelectComponent = React.RefForwardingComponent<HTMLSelectElement, SelectProps>

const MultiSelect: MultiSelectComponent = (props, ref) => {

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    fieldProps: { icon = 'arrow_drop_down', iconPosition = 'right', iconSize = 32, ...fieldProps },
    inputProps: { options, defaultOptions, optionChange, placeholder }
  } = useField<MultiSelectPropsDefault>(props);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelectAll = () => {
    if (selectedOptions.length === options.length) {
      setSelectedOptions([]);
    } else {
      setSelectedOptions(options.map(option => option.value));
    }
  };

  const handleOptionToggle = (value: string) => {
    if (selectedOptions.includes(value)) {
      setSelectedOptions(selectedOptions.filter(v => v !== value));
    } else {
      setSelectedOptions([...selectedOptions, value]);
    }
  };

  const isAllSelected = selectedOptions.length === options.length;

  const filteredOptions = options.filter(option =>
    option.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    setSelectedOptions(defaultOptions);
  }, [defaultOptions]);

  useEffect(() => {
    optionChange(selectedOptions);
  }, [selectedOptions, optionChange]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const currentRef = dropdownRef?.current as HTMLElement | null;
      if (currentRef && !currentRef.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <Field
      icon={icon}
      iconPosition={iconPosition}
      iconSize={iconSize}
      {...fieldProps}
    >
      <div ref={dropdownRef} className={classes['custom-dropdown']}>
        <div
          className={classes['custom-dropdown__toggle']}
          onClick={toggleDropdown}
          role="button"
          aria-expanded={isOpen}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') toggleDropdown();
          }}
        >
          <span>{placeholder}</span>
          <span className={classes['custom-dropdown__count']}>{selectedOptions.length}/{options.length}</span>
        </div>

        {isOpen && (
          <div className={classes['custom-dropdown__menu']}>
            <div className={classes['custom-dropdown__search-container']}>
              <input
                type="text"
                inputMode="text"
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className={classes['custom-dropdown__search']}
              />
            </div>

            <label className={classes['custom-dropdown__option']}>
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleSelectAll}
              />
              {isAllSelected ? ' Deselect All' : ' Select All'}
            </label>

            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <label key={option.value} className={classes['custom-dropdown__option']}>
                  <input
                    type="checkbox"
                    checked={selectedOptions.includes(option.value)}
                    onChange={() => handleOptionToggle(option.value)}
                  />
                  {' ' + option.text}
                </label>
              ))
            ) : (
              <div className={classes['custom-dropdown__no-results']}>No results found</div>
            )}
          </div>
        )}
      </div>
    </Field>
  );
};

export default memo(React.forwardRef(MultiSelect))
